import { useCallback, useRef, useState } from 'react';
import {
  getTableState,
  setTableState,
  captureAgGridState,
  restoreAgGridState,
  applyPersistedTableStateToColumnDefs,
} from './tableStatePersistence';

const SAVE_DEBOUNCE_MS = 300;

export type UseAgGridStatePersistenceResult = {
  augmentGridReady: (
    params: any,
    userOnGridReady?: (params: any) => void
  ) => void;
  appliedSort: { field: string; order: string } | null;
  mergeColumnDefs: (defs: any[]) => any[];
  /** True until persisted filters/sort are applied (or there is nothing to restore). */
  ignoreRestoreQueryEventsRef: { current: boolean };
  /** Allow filter/sort handlers to refetch (e.g. URL-driven filter before restore timeout). */
  releaseRestoreQueryEvents: () => void;
  /** Pass to AgGridReact so dragging a column header off the grid does not hide the column. */
  suppressDragLeaveHidesColumns: true;
};

export function useAgGridStatePersistence(
  tableId: string | undefined
): UseAgGridStatePersistenceResult {
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ignoreRestoreQueryEventsRef = useRef(true);
  const [appliedSort, setAppliedSort] = useState<{ field: string; order: string } | null>(null);

  const mergeColumnDefs = useCallback(
    (defs: any[]) => applyPersistedTableStateToColumnDefs(defs, tableId, appliedSort),
    [tableId, appliedSort]
  );

  const releaseRestoreQueryEvents = useCallback(() => {
    ignoreRestoreQueryEventsRef.current = false;
  }, []);

  const saveState = useCallback((api: any) => {
    if (!tableId || !api) return;
    try {
      const state = captureAgGridState(api);
      setTableState(tableId, state);
    } catch {
      // ignore
    }
  }, [tableId]);

  const debouncedSaveState = useCallback((api: any) => {
    if (!tableId || !api) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveState(api);
    }, SAVE_DEBOUNCE_MS);
  }, [tableId, saveState]);

  const augmentGridReady = useCallback(
    (params: any, userOnGridReady?: (params: any) => void) => {
      const api = params?.api;
      if (!api) {
        ignoreRestoreQueryEventsRef.current = false;
        userOnGridReady?.(params);
        return;
      }

      const apiWithOpts = api as { setGridOption?: (key: string, value: unknown) => void };
      apiWithOpts.setGridOption?.('suppressDragLeaveHidesColumns', true);

      if (tableId) {
        const saved = getTableState(tableId);
        if (saved && Object.keys(saved).length > 0) {
          const doRestore = () => {
            restoreAgGridState(api, saved);
            if (saved.sort?.field && saved.sort?.order) setAppliedSort(saved.sort);
            setTimeout(releaseRestoreQueryEvents, 0);
          };
          setTimeout(doRestore, 0);
        } else {
          setTimeout(releaseRestoreQueryEvents, 0);
        }
        const saveOnChange = () => debouncedSaveState(api);
        api.addEventListener?.('filterChanged', saveOnChange);
        api.addEventListener?.('sortChanged', () => {
          saveOnChange();
          const model = api.getSortModel?.();
          if (model?.[0]) setAppliedSort({ field: model[0].colId, order: model[0].sort });
          else setAppliedSort(null);
        });
        api.addEventListener?.('columnResized', saveOnChange);
        api.addEventListener?.('columnMoved', saveOnChange);
        api.addEventListener?.('columnVisible', saveOnChange);
      } else {
        ignoreRestoreQueryEventsRef.current = false;
      }

      userOnGridReady?.(params);
    },
    [tableId, debouncedSaveState, releaseRestoreQueryEvents]
  );

  return {
    augmentGridReady,
    appliedSort,
    mergeColumnDefs,
    ignoreRestoreQueryEventsRef,
    releaseRestoreQueryEvents,
    suppressDragLeaveHidesColumns: true,
  };
}
